import type { ReactElement } from 'react';
import type { ChartWidgetData } from '@trinity/chat/types/dashboard-data';
import { NivoBarChart } from '@trinity/chat-components/components/Widgets/Charts/Bar';
import { NivoLineChart } from '@trinity/chat-components/components/Widgets/Charts/Line';
import { NivoMultipleBarChart } from '@trinity/chat-components/components/Widgets/Charts/MultipleBar';
import { NivoPieChart } from '@trinity/chat-components/components/Widgets/Charts/Pie';
import { NivoStackedBarChart } from '@trinity/chat-components/components/Widgets/Charts/StackedBar';
import type { ChartDataOf } from './chart-kinds';

/**
 * The one place the chart kinds that are drawn by a charting library are turned into chart elements.
 * The dashboard chart widget, the quant-analysis chart and the citation preview all draw through
 * `renderNivoChart`, so swapping the charting library means changing this file and the adapters it
 * calls, not each of those views. What each view wraps around the chart (legend, insights, overrides)
 * stays in the view.
 */

export type NivoChartData = ChartDataOf<
  'bar' | 'stacked-bar' | 'pie' | 'multiple-clustered-bar' | 'multiple-stacked-bar' | 'line' | 'multiple-line'
>;

/** What a view may adjust. Anything left undefined takes the chart component's own default. */
export interface NivoChartOptions {
  animate?: boolean;
  columnN?: number;
  unit?: string;
  isPrefix?: boolean;
  seriesToN?: Record<string, number>;
  isInteractive?: boolean;
}

/** Whether a chart's data is drawn by `renderNivoChart` (the others are tables and cards). */
export function isNivoChartData(chart: ChartWidgetData): chart is NivoChartData {
  switch (chart.type) {
    case 'bar':
    case 'stacked-bar':
    case 'pie':
    case 'multiple-clustered-bar':
    case 'multiple-stacked-bar':
    case 'line':
    case 'multiple-line':
      return true;
    case 'data-table':
    case 'kpi-widget':
    case 'progress-bar-widget':
    case 'semi-circle-progress-widget':
      return false;
    default: {
      const _exhaustive: never = chart;
      return false;
    }
  }
}

/** The unit, prefix and per-series counts a chart's own `options` ask for, which most views pass straight through. */
export function nivoOptionsFromChart(chart: NivoChartData): NivoChartOptions {
  switch (chart.type) {
    case 'bar':
    case 'stacked-bar':
    case 'pie':
      return { unit: chart.options?.unit, isPrefix: chart.options?.isPrefix };
    case 'multiple-clustered-bar':
    case 'multiple-stacked-bar':
      return { unit: chart.options?.unit, isPrefix: chart.options?.isPrefix, seriesToN: chart.options?.seriesToN };
    case 'line':
    case 'multiple-line':
      return {};
    default: {
      const _exhaustive: never = chart;
      return {};
    }
  }
}

export function renderNivoChart(chart: NivoChartData, colors: string[], options: NivoChartOptions = {}): ReactElement {
  const { animate, columnN, unit, isPrefix, seriesToN, isInteractive } = options;
  switch (chart.type) {
    case 'bar':
      return (
        <NivoBarChart
          chart={chart.chart}
          colors={colors}
          animate={animate}
          columnN={columnN}
          unit={unit}
          isPrefix={isPrefix}
          isInteractive={isInteractive}
        />
      );
    case 'stacked-bar':
      return (
        <NivoStackedBarChart
          chart={chart.chart}
          colors={colors}
          animate={animate}
          columnN={columnN}
          unit={unit}
          isPrefix={isPrefix}
          isInteractive={isInteractive}
        />
      );
    case 'pie':
      return (
        <NivoPieChart
          chart={chart.chart}
          colors={colors}
          animate={animate}
          columnN={columnN}
          unit={unit}
          isPrefix={isPrefix}
          isInteractive={isInteractive}
        />
      );
    case 'multiple-clustered-bar':
    case 'multiple-stacked-bar':
      return (
        <NivoMultipleBarChart
          isStacked={chart.type === 'multiple-stacked-bar'}
          chart={chart.chart}
          colors={colors}
          animate={animate}
          seriesToN={seriesToN}
          unit={unit}
          isPrefix={isPrefix}
          isInteractive={isInteractive}
        />
      );
    case 'line':
    case 'multiple-line':
      return <NivoLineChart chart={chart.chart} colors={colors} columnN={columnN} />;
    default: {
      // A chart kind drawn by a charting library without a case here is a type error.
      const _exhaustive: never = chart;
      throw new Error('Unsupported chart type');
    }
  }
}
